import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Modal } from '@/components/ui/Modal';
import { describeError } from '@/lib/http';

import {
  fetchClientSuggestions,
  fetchDirectoryUsers,
  type Transcription,
  type UpdateMeetingInput,
} from '../api';
import { ClientNameCombobox } from './ClientNameCombobox';
import { MeetingDatePicker } from './MeetingDatePicker';
import { ParticipantChips } from './ParticipantChips';
import { MEETING_TYPE_OPTIONS, todayMeetingDate, type MeetingType } from '../meetingLabels';
import { useUpdateMeetingMutation } from '../queries';

type Props = {
  open: boolean;
  meeting: Transcription | null;
  onClose: () => void;
};

export function EditMeetingModal({ open, meeting, onClose }: Props) {
  const updateMutation = useUpdateMeetingMutation();
  const usersQuery = useQuery({
    queryKey: ['directory-users'],
    queryFn: ({ signal }) => fetchDirectoryUsers(signal),
    staleTime: 60_000,
    enabled: open,
  });

  const [title, setTitle] = useState('');
  const [meetingType, setMeetingType] = useState<MeetingType>('CLIENT_WORK');
  const [meetingDate, setMeetingDate] = useState(todayMeetingDate());
  const [clientName, setClientName] = useState('');
  const [axiumParticipantIds, setAxiumParticipantIds] = useState<string[]>([]);
  const [clientParticipants, setClientParticipants] = useState<string[]>([]);
  const [axiumSearch, setAxiumSearch] = useState('');
  const [clientParticipantDraft, setClientParticipantDraft] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || meeting === null) {
      return;
    }
    setTitle(meeting.title ?? '');
    setMeetingType(
      meeting.meetingType === 'PRE_SALE' ||
        meeting.meetingType === 'CLIENT_WORK' ||
        meeting.meetingType === 'INTERNAL'
        ? meeting.meetingType
        : 'CLIENT_WORK',
    );
    setMeetingDate(meeting.meetingDate ?? todayMeetingDate());
    setClientName(meeting.clientName ?? '');
    setAxiumParticipantIds(meeting.axiumParticipants.map((item) => item.id));
    setClientParticipants([...meeting.clientParticipantNames]);
    setAxiumSearch('');
    setClientParticipantDraft('');
    setFormError(null);
  }, [open, meeting]);

  const showClientFields = meetingType !== 'INTERNAL';
  const clientSuggestionsQuery = useQuery({
    queryKey: ['client-suggestions', clientName],
    queryFn: ({ signal }) => fetchClientSuggestions(clientName, signal),
    enabled: open && showClientFields,
    staleTime: 30_000,
  });

  const selectedAxium = useMemo(() => {
    const users = usersQuery.data ?? [];
    const byId = new Map(users.map((user) => [user.id, user]));
    return axiumParticipantIds.map((id) => {
      const fromDirectory = byId.get(id);
      if (fromDirectory !== undefined) {
        return fromDirectory;
      }
      const fromMeeting = meeting?.axiumParticipants.find((item) => item.id === id);
      return {
        id,
        displayName: fromMeeting?.displayName ?? id,
        email: '',
        authProvider: 'LOCAL',
      };
    });
  }, [axiumParticipantIds, meeting?.axiumParticipants, usersQuery.data]);

  const filteredUsers = useMemo(() => {
    const q = axiumSearch.trim().toLowerCase();
    const selected = new Set(axiumParticipantIds);
    return (usersQuery.data ?? []).filter((user) => {
      if (selected.has(user.id)) {
        return false;
      }
      if (q === '') {
        return true;
      }
      return user.displayName.toLowerCase().includes(q) || user.email.toLowerCase().includes(q);
    });
  }, [axiumParticipantIds, axiumSearch, usersQuery.data]);

  const addClientParticipant = () => {
    const name = clientParticipantDraft.trim();
    if (name === '') {
      return;
    }
    setClientParticipants((prev) => (prev.includes(name) ? prev : [...prev, name]));
    setClientParticipantDraft('');
  };

  const onSave = async () => {
    if (meeting === null) {
      return;
    }
    setFormError(null);
    const trimmedTitle = title.trim();
    if (trimmedTitle === '') {
      setFormError('Укажите название встречи');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/u.test(meetingDate)) {
      setFormError('Укажите дату встречи');
      return;
    }
    if (showClientFields && clientName.trim() === '') {
      setFormError('Укажите наименование клиента');
      return;
    }
    const body: UpdateMeetingInput = {
      title: trimmedTitle,
      meetingType,
      meetingDate,
      clientName: showClientFields ? clientName.trim() : null,
      axiumParticipantIds,
      clientParticipantNames: showClientFields ? clientParticipants : [],
    };
    try {
      await updateMutation.mutateAsync({ id: meeting.id, body });
      onClose();
    } catch (cause) {
      setFormError(describeError(cause));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Редактировать встречу"
      subtitle="Изменяются только сведения о встрече"
      width="720px"
    >
      <div className="form stack">
        <Field htmlFor="edit-title" label="Название встречи">
          <input
            id="edit-title"
            className="field__control"
            type="text"
            value={title}
            disabled={updateMutation.isPending}
            onChange={(event) => setTitle(event.target.value)}
          />
        </Field>
        <Field htmlFor="edit-meetingType" label="Тип встречи">
          <select
            id="edit-meetingType"
            className="field__control"
            value={meetingType}
            disabled={updateMutation.isPending}
            onChange={(event) => setMeetingType(event.target.value as MeetingType)}
          >
            {MEETING_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>
        <Field htmlFor="edit-meetingDate" label="Дата встречи">
          <MeetingDatePicker
            id="edit-meetingDate"
            value={meetingDate}
            disabled={updateMutation.isPending}
            onChange={setMeetingDate}
          />
        </Field>
        {showClientFields ? (
          <Field htmlFor="edit-clientName" label="Клиент">
            <ClientNameCombobox
              id="edit-clientName"
              value={clientName}
              disabled={updateMutation.isPending}
              suggestions={clientSuggestionsQuery.data ?? []}
              onChange={setClientName}
            />
          </Field>
        ) : null}
        <Field htmlFor="edit-axium-search" label="Участники AXIUM">
          <input
            id="edit-axium-search"
            className="field__control"
            type="search"
            placeholder="Поиск сотрудника"
            value={axiumSearch}
            disabled={updateMutation.isPending}
            onChange={(event) => setAxiumSearch(event.target.value)}
          />
          {filteredUsers.length > 0 ? (
            <ul className="picker-list" aria-label="Доступные сотрудники">
              {filteredUsers.slice(0, 8).map((user) => (
                <li key={user.id}>
                  <button
                    type="button"
                    className="picker-list__item"
                    disabled={updateMutation.isPending}
                    onClick={() => {
                      setAxiumParticipantIds((prev) => [...prev, user.id]);
                      setAxiumSearch('');
                    }}
                  >
                    {user.displayName}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <ParticipantChips
            ariaLabel="Выбранные участники AXIUM"
            items={selectedAxium.map((user) => ({ id: user.id, label: user.displayName }))}
            onRemove={(id) => {
              setAxiumParticipantIds((prev) => prev.filter((item) => item !== id));
            }}
          />
        </Field>
        {showClientFields ? (
          <Field htmlFor="edit-client-participant" label="Участники клиента" hint="Введите имя и нажмите Enter.">
            <input
              id="edit-client-participant"
              className="field__control"
              type="text"
              value={clientParticipantDraft}
              disabled={updateMutation.isPending}
              onChange={(event) => setClientParticipantDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addClientParticipant();
                }
              }}
            />
            <ParticipantChips
              ariaLabel="Участники клиента"
              items={clientParticipants.map((name) => ({ id: name, label: name }))}
              onRemove={(id) => {
                setClientParticipants((prev) => prev.filter((item) => item !== id));
              }}
            />
          </Field>
        ) : null}
        {formError !== null ? (
          <p className="chatgpt-result-form__error" role="alert">
            {formError}
          </p>
        ) : null}
        <div className="chatgpt-result-form__actions">
          <Button
            type="button"
            variant="primary"
            disabled={updateMutation.isPending}
            onClick={() => {
              void onSave();
            }}
          >
            {updateMutation.isPending ? 'Сохранение…' : 'Сохранить'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={updateMutation.isPending}
            onClick={onClose}
          >
            Отмена
          </Button>
        </div>
      </div>
    </Modal>
  );
}
